import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { isEqual } from "lodash";

import { CHBA_RENO_COMM_ID } from "utils/communities/api";
import { timeDifference } from "utils";
import { getSortableName } from "utils/directory/sorting";
import { COMMUNITY_RUNS_PER_CREDIT } from "utils/communities/constants";
import { TRANSACTIONS_TYPES, URL } from "utils/payment/constants";

import SectionHeading from "components/SectionHeading";
import UnlockRunsModal from "components/UnlockRunsModal";
import CommunityRunCapacityHeader from "../CommunityRunCapacityHeader";
import TopButtons from "../TopButtons";
import { useCommunityRunStats } from "../hooks/useCommunityRunStats";
import Tabs from "components/Tabs";
import RenoModels from "../RenoModels";
import CustomModels from "../CustomModels";
import Upgrades from "../Upgrades";
import Configuration from "../Configuration";
import CHBAResults from "../CHBAResults";
import Results from "../Results";
import CommunityTabValidationOverlay from "../CommunityTabValidationOverlay";
import { CommunityValidationLockProvider } from "../CommunityValidationLockContext";
import { RenameDialog } from "components/Dashboard/Actions";

import { change } from "store/root/actions";
import {
    selectCommunityDetails,
    selectCommunityError,
    selectCommunityLoading,
    selectCommunityModelDir,
    selectCommunityLastValidationRun,
    selectCommunityEcmInputMap,
    selectCommunityRunHistory,
    selectModelUpgradesRun,
} from "store/community/selectors";
import { selectUpgradePackagesSortedArray } from "../Upgrades/_ducks/selectors";
import { selectUserCreditSummary } from "store/users/selectors";
import { fetchCommunityRunHistory, renameCommunity } from "store/community/thunk";

import classes from "../style.module.scss";

import Edit from "assets/images/icons/JSX/Edit";
import CommModelsIcon from "assets/images/icons/CommModels.svg";
import UpgradeIcon from "assets/images/icons/Upgrades.svg";
import ConfigurationIcon from "assets/images/icons/Optimizations.svg";
import ResultsIcon from "assets/images/icons/Results.svg";

const CommTabs = React.memo(
    ({
        uid,
        communityId,
        history,
        pathCallback,
        communityDetails,
        commModelDir,
        communityLoading,
        error,
        change,
        renameCommunity,
        lastValidationRun,
        ecmInputMap,
        upgradePackages,
        runHistory,
        modelUpgradesRun,
        creditSummary,
        fetchCommunityRunHistory,
    }) => {
        const [intialPath, initialTab] = window.location.pathname.split(communityId);
        const [tab, changeTab] = useState(initialTab);
        const [isUnlockRunsOpen, setIsUnlockRunsOpen] = useState(false);
        const isCHBAComm = communityId === CHBA_RENO_COMM_ID;

        const { name, totalModels, region, lastEdited, editedName, createdAt, owner = {}, availableRuns = 0 } =
            communityDetails;
        const { total: legacyTotalCredits = 0, credits: totalCredits = 0 } = creditSummary || {};
        const editedDateTime = lastEdited; // usually lastEdited is an object but for RenoComm it's just a date

        const { name: ownerName = "", uid: ownerId = uid } = owner;
        // const { name: editedName, datetime: editedDateTime } = lastEdited;

        const [nameDialogOpen, toggleNameDialog] = useState(false);
        const [newName, setNewName] = useState(name);

        // Update tab to match the current pathname when the browser's back button is pressed
        useEffect(() => {
            const [path, prevTab] = pathCallback().split(communityId);
            return changeTab(prevTab);
        }, [pathCallback]);

        const { models = {} } = commModelDir || {};

        const readyModels = useMemo(
            () =>
                Object.entries(models)
                    .filter(([, m]) => m.status === "ready")
                    .sort(([_, a], [__, b]) => getSortableName(a?.name).localeCompare(getSortableName(b?.name))),
            [models]
        );

        const { runInfo, runsUsedTotal } = useCommunityRunStats({
            communityId,
            fetchCommunityRunHistory,
            readyModels,
            upgradePackages,
            ecmInputMap,
            modelUpgradesRun,
            runHistory,
        });

        return (
            <>
                <SectionHeading
                    title={
                        isCHBAComm ? (
                            name
                        ) : (
                            <>
                                {name}
                                <span
                                    style={{ marginLeft: 8, cursor: "pointer" }}
                                    onClick={() => {
                                        toggleNameDialog(true);
                                    }}
                                >
                                    <Edit fill="#646f81" className="notoggle" />
                                </span>
                            </>
                        )
                    }
                    subtitle={
                        editedDateTime ? (
                            <span>
                                Last edited{" "}
                                {
                                    <strong>
                                        {timeDifference({
                                            start: new Date(),
                                            end: editedDateTime,
                                        })}
                                    </strong>
                                }{" "}
                                ago{editedName ? ` by ${editedName}.` : "."}
                            </span>
                        ) : (
                            <span>
                                Created{" "}
                                {
                                    <strong>
                                        {timeDifference({
                                            start: new Date(),
                                            end: createdAt,
                                        })}
                                    </strong>
                                }{" "}
                                ago{ownerName ? ` by ${ownerName}.` : "."}
                            </span>
                        )
                    }
                    sideContent={
                        isCHBAComm ? (
                            <TopButtons currentTab={tab} readOnly />
                        ) : (
                            <CommunityRunCapacityHeader
                                currentTab={tab}
                                availableRuns={availableRuns}
                                runsUsedTotal={runsUsedTotal}
                                runsToBeUsed={runInfo.numToBeRun || 0}
                                onUnlockRunsClick={() => setIsUnlockRunsOpen(true)}
                            />
                        )
                    }
                />

                <CommunityValidationLockProvider lastValidationRun={lastValidationRun}>
                    <div className={classes.modelWrapper}>
                        {error && <p>{error}</p>}
                        <Tabs
                            initialTab={initialTab || "/"}
                            tabActive={tab || "/"}
                            tabClassName={classes.tabWrapper}
                            tabNavClassName={classes.tabNavWrapper}
                            tabs={[
                            {
                                name: "Community Models",
                                url: "/",
                                icon: CommModelsIcon,
                            },
                            {
                                name: "Upgrades",
                                url: "/upgrades",
                                disabled: isCHBAComm,
                                info: isCHBAComm ? "This feature is not available for this community type" : "",
                                icon: UpgradeIcon,
                            },
                            {
                                name: "Configuration",
                                url: "/configuration",
                                disabled: isCHBAComm,
                                info: isCHBAComm ? "This feature is not available for this community type" : "",
                                icon: ConfigurationIcon,
                            },
                            {
                                name: "Results",
                                url: "/results",
                                icon: ResultsIcon,
                            },
                        ]}
                        onChange={(e) => {
                            history.push(`/communities/${communityId}${e}`);
                            return changeTab(e);
                        }}
                        stretchTabs
                    >
                        {isCHBAComm ? (
                            <div className={classes.tabPanelWithLock}>
                                <RenoModels history={history} commModelDir={commModelDir} />
                                <CommunityTabValidationOverlay />
                            </div>
                        ) : (
                            <div className={classes.tabPanelWithLock}>
                                <CustomModels history={history} commModelDir={commModelDir} change={change} />
                                <CommunityTabValidationOverlay />
                            </div>
                        )}
                        <div className={classes.tabPanelWithLock}>
                            <Upgrades history={history} change={change} />
                            <CommunityTabValidationOverlay />
                        </div>
                        <Configuration
                            history={history}
                            uid={uid}
                            communityId={communityId}
                            onUnlockRunsClick={() => setIsUnlockRunsOpen(true)}
                        />
                        {isCHBAComm ? (
                            <CHBAResults history={history} commModelDir={commModelDir} />
                        ) : (
                            <Results history={history} commModelDir={commModelDir} />
                        )}
                        </Tabs>
                    </div>
                </CommunityValidationLockProvider>
                {!isCHBAComm && (
                    <UnlockRunsModal
                        isOpen={isUnlockRunsOpen}
                        onClose={() => setIsUnlockRunsOpen(false)}
                        subjectId={communityId}
                        subjectName={name}
                        userUid={uid}
                        legacyTotalCredits={legacyTotalCredits}
                        newTotalCredits={totalCredits}
                        availableRuns={availableRuns}
                        totalCombinations={runInfo.totalSimulations || 0}
                        runsUsedTotal={runsUsedTotal}
                        runsToBeUsed={runInfo.numToBeRun || 0}
                        runsPerCredit={COMMUNITY_RUNS_PER_CREDIT}
                        transactionType={TRANSACTIONS_TYPES["SPENT.COMMUNITY"]}
                        spentFeature="COMMUNITY"
                        spentUrl={URL.COMMUNITY}
                        analyticsLabel="Community"
                        subjectLabel="community"
                        capacityTitle="Community capacity"
                        allowLegacyCredits={false}
                    />
                )}
                <RenameDialog
                    itemType={"community"}
                    openDialog={nameDialogOpen}
                    oldName={name}
                    newName={newName}
                    setNewName={setNewName}
                    onClick={() => {
                        renameCommunity(newName);
                        toggleNameDialog(false);
                    }}
                    onClose={() => {
                        setNewName(name);
                        toggleNameDialog(false);
                    }}
                    isLoading={communityLoading}
                />
            </>
        );
    },
    isEqual
);

const mapStateToProps = createStructuredSelector({
    communityDetails: selectCommunityDetails,
    commModelDir: selectCommunityModelDir,
    communityLoading: selectCommunityLoading,
    error: selectCommunityError,
    lastValidationRun: selectCommunityLastValidationRun,
    ecmInputMap: selectCommunityEcmInputMap,
    upgradePackages: selectUpgradePackagesSortedArray,
    runHistory: selectCommunityRunHistory,
    modelUpgradesRun: selectModelUpgradesRun,
    creditSummary: selectUserCreditSummary,
});

const mapDispatchToProps = (dispatch) => ({
    change: (path, value) => dispatch(change(path, value)),
    fetchCommunityRunHistory: async (id) => dispatch(fetchCommunityRunHistory(id)),
    renameCommunity: async (newCommunityName) => {
        await dispatch(renameCommunity(newCommunityName));
    },
});

export default connect(mapStateToProps, mapDispatchToProps)(CommTabs);
